"use client";

import type { keyPair } from "@repo/models";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { styles } from "@repo/helpers";
import { Circle } from "lucide-react";
import * as React from "react";

import { InputComponent } from "../input";
import { Label } from "../label";

interface RadioGroupProps {
	id?: string;
	label?: string;
	value: string;
	changeHandler: (value: string) => void;
	values: keyPair[];
	hidden?: boolean;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({ id, label, value, changeHandler, values, hidden }) => {
	return (
		<InputComponent id={id} label={label} hidden={hidden}>
			<RadioGroupInternal id={id} value={value?.toString()} onValueChange={changeHandler}>
				{values.map((option) => (
					<div className="flex items-center space-x-2" key={option.id}>
						<RadioGroupItem value={option.id.toString()} id={option.id.toString()} />
						<Label htmlFor={option.id.toString()}>{option.value}</Label>
					</div>
				))}
			</RadioGroupInternal>
		</InputComponent>
	);
};

const RadioGroupInternal = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
	return <RadioGroupPrimitive.Root className={styles("grid gap-2", className)} {...props} ref={ref} />;
});
RadioGroupInternal.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
	return (
		<RadioGroupPrimitive.Item
			ref={ref}
			className={styles(
				"aspect-square h-4 w-4 rounded-full border border-primary text-primary shadow focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
				className,
			)}
			{...props}
		>
			<RadioGroupPrimitive.Indicator className="flex items-center justify-center">
				<Circle className="h-3.5 w-3.5 fill-primary" />
			</RadioGroupPrimitive.Indicator>
		</RadioGroupPrimitive.Item>
	);
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

// export { RadioGroupInternal, RadioGroupItem };
